import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  addTagsToObjectInRepository,
  resetTagEditor,
  selectTagEditorId,
  selectTagEditorObjectType,
  selectTagEditorTags,
  setTagEditorTags,
  setTagEditorVisible,
} from '../../store/slices/tag-editor.slice';
import {
  MultiSelect,
  MultiSelectChangeEvent,
  MultiSelectFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { useGetTagListQuery, useSaveTagsMutation } from '../../store/api/tags.api';
import { Tag, TagEditorEntity } from '../../models/tag.model';
import { Checkbox } from '@progress/kendo-react-inputs';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';

export function TagEditor() {
  const dispatch = useAppDispatch();
  const tags = useAppSelector(selectTagEditorTags);
  const objectId = useAppSelector(selectTagEditorId);
  const objectType = useAppSelector(selectTagEditorObjectType);
  const [saveTags] = useSaveTagsMutation();
  const [open, setOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const { data, isLoading, error, refetch } = useGetTagListQuery(objectId);
  const [value, setValue] = useState<string>('');
  useEffect(() => {
    if (!isLoading && !error && data && data.length > 0) {
      setSuggestions(
        data.map((tag: any): Tag => {
          return {
            id: tag.name,
            text: tag.name,
          };
        })
      );
    }
  }, [data, isLoading, error]);

  const setTags = useCallback(
    (tags: Tag[]) => {
      dispatch(setTagEditorTags(tags));
    },
    [dispatch]
  );

  const handleDelete = (i: number) => {
    setTags(tags.filter((_, index) => index !== i));
  };

  const handleAddition = (tag: Tag) => {
    setTags([...tags, tag]);
    const found = suggestions.filter((t) => t.id === tag.id);
    if (found.length == 0) {
      const nextSuggestions = [...suggestions, tag];
      setSuggestions(nextSuggestions);
    }
  };

  const changeTags = (e: MultiSelectChangeEvent) => {
    const nextTags = e.value.map((tag: Partial<Tag>) => {
      return {
        id: tag.text,
        text: tag.text,
      };
    });
    setTags(nextTags);
    const nextSuggestions = [...suggestions];
    nextTags.forEach((tag: Tag) => {
      const found = suggestions.filter((t) => t.id === tag.id);
      if (found.length == 0) {
        nextSuggestions.push(tag);
      }
    });
    setSuggestions(nextSuggestions);
  };

  const onFilterChange = (e: MultiSelectFilterChangeEvent) => {
    const inputEvent = e.nativeEvent as InputEvent;
    const { filter } = e;
    if (inputEvent.data == ',') {
      const tag = filter?.value?.replace(',', '');
      if (tag) {
        handleAddition({ text: tag, id: tag });
        setValue('');
      }
    } else {
      setValue(filter.value);
    }
  };

  const closeTagEditor = () => {
    dispatch(setTagEditorVisible(false));
  };

  const onSave = useCallback(() => {
    const targetIds = !Array.isArray(objectId) ? [objectId] : objectId;
    const tagPayload = tags.map((tag: Tag) => ({
      name: tag.text,
      value: null,
    }));
    saveTags({
      objectType: objectType ? objectType : TagEditorEntity.RESPONSE,
      objectIds: targetIds,
      tags: tagPayload,
    })
      .then((response) => {
        const tagsToAdd: { [key: number]: string } = {};
        // @ts-expect-error lack of typings
        response.data.forEach((tag: any): void => {
          tagsToAdd[tag.userTeamTagId] = tag.tag;
        });
        dispatch(
          addTagsToObjectInRepository({
            repository: TagEditorEntity.RESPONSE as string,
            ids: targetIds,
            tags: tagsToAdd,
          })
        );
        dispatch(resetTagEditor());
      })
      .catch((e) => console.error(e));
  }, [dispatch, objectId, objectType, saveTags, tags]);

  useEffect(() => {
    refetch().catch((e) => console.error(e));
    return () => setSuggestions([]);
  }, [refetch]);

  const onItemClick = useCallback(
    (item: Tag) => {
      if (tags) {
        const query = tags.filter((tag) => tag.id === item.id);
        if (query.length == 0) {
          setTags([...tags, item]);
        } else {
          const index = tags.findIndex((tag) => tag.id == item.id);
          const nextTags = [...tags];
          if (index > -1) {
            nextTags.splice(index, 1);
            setTags(nextTags);
          }
        }
      }
    },
    [tags, setTags]
  );

  return (
    <Dialog
      title={'Add Tag'}
      onClose={closeTagEditor}
      className="tag-editor-input-dialog"
      closeIcon={true}
    >
      <p>Enter tags and separate with commas to add multiple.</p>
      <div className="tag-editor-input">
        <MultiSelect
          value={tags}
          onChange={(e: MultiSelectChangeEvent) => changeTags(e)}
          allowCustom={true}
          filter={value}
          filterable={true}
          textField={'text'}
          dataItemKey="id"
          autoClose={true}
          data={suggestions}
          onFilterChange={(e: MultiSelectFilterChangeEvent) => onFilterChange(e)}
          tagRender={() => <></>}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          suffix={() => {
            return (
              <div className="input-suffix">
                <KwelloSvgIcon
                  icon={open ? KWELLO_ICON.CHEVRON_BIG_UP_ICON : KWELLO_ICON.CHEVRON_BIG_DOWN_ICON}
                />
              </div>
            );
          }}
          itemRender={(_, itemProps: any) => {
            const item = itemProps.dataItem;
            return (
              <li
                id={itemProps.id}
                className={'k-list-item'}
                onClick={(e) => {
                  e.stopPropagation();
                  onItemClick(item as Tag);
                }}
              >
                <Checkbox checked={itemProps.selected} />
                {item[itemProps.textField as string]}
              </li>
            );
          }}
        />
        <div className="chip-list">
          {tags.length > 0 &&
            tags.map((tag, index) => {
              return (
                <Chip
                  key={index}
                  onRemove={() => handleDelete(index)}
                  removable
                  className="k-chip k-chip-sm k-chip-tag-editor tag-user"
                >
                  {tag.text}
                </Chip>
              );
            })}
        </div>
      </div>
      <DialogActionsBar>
        <Button
          onClick={() => onSave()}
          fillMode={'solid'}
          themeColor={'secondary'}
          className="react-tags-button"
        >
          Add
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
}
